import React, { useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { api, formatMoney, fileUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Minus, Plus, Trash2, ShoppingBag, Sandwich, Send, ShieldOff,
} from "lucide-react";
import { toast } from "sonner";

export default function ClienteCarrito() {
  const cart = useCart();
  const navigate = useNavigate();
  const outlet = useOutletContext() || {};
  const { refresh } = useAuth();
  const suspended = !!outlet.suspended;
  const [nota, setNota] = useState("");
  const [sending, setSending] = useState(false);

  const enviar = async () => {
    if (suspended) {
      toast.error("Cuenta suspendida. Hable con el vendedor para regularizar su situación.");
      return;
    }
    if (cart.items.length === 0) {
      toast.error("Tu carrito está vacío.");
      return;
    }
    setSending(true);
    try {
      const res = await api.post("/pedidos/mio", {
        items: cart.items.map((it) => ({ producto_id: it.producto_id, cantidad: it.cantidad })),
        nota: nota.trim(),
      });
      cart.clear();
      toast.success(`Pedido ${res.data.pedido_id} enviado. ¡Gracias!`);
      navigate(`/cliente/pedidos/${res.data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al enviar el pedido.";
      toast.error(detail);
      // If server says suspended (race between suspension + client cached state),
      // refresh /me so the banner + guard reflect the new status immediately.
      if (e?.response?.status === 403 && /suspendida/i.test(detail)) {
        refresh?.();
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <Link to="/cliente" className="inline-flex items-center gap-2 text-sm text-[#595959] hover:text-[#C85A32] mb-4" data-testid="cli-cart-back">
        <ArrowLeft className="h-4 w-4" /> Seguir comprando
      </Link>

      <div className="text-[11px] uppercase tracking-[0.22em] text-[#C85A32] font-semibold mb-2">Tu carrito</div>
      <h1 className="font-display font-bold text-3xl sm:text-5xl tracking-tight mb-6">Confirmar pedido</h1>

      {cart.items.length === 0 ? (
        <Card className="border border-dashed border-[#E5E0D8] rounded-xl p-10 text-center bg-white" data-testid="cli-cart-empty">
          <Sandwich className="h-12 w-12 mx-auto text-[#C85A32] mb-3 opacity-60" />
          <div className="font-display font-semibold text-lg">Tu carrito está vacío</div>
          <p className="text-sm text-[#595959] mt-1 mb-4">Sumá algunos productos del catálogo.</p>
          <Link to="/cliente">
            <Button className="bg-[#C85A32] hover:bg-[#A64A29] text-white gap-2">
              <ShoppingBag className="h-4 w-4" /> Ir al catálogo
            </Button>
          </Link>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 border-[#E5E0D8] rounded-2xl bg-white overflow-hidden">
            <ul className="divide-y divide-[#F0EBE0]">
              {cart.items.map((it, idx) => (
                <li key={it.producto_id} className="p-4 flex items-center gap-3 fade-row" style={{ animationDelay: `${idx * 30}ms` }} data-testid={`cli-cart-row-${it.producto_id}`}>
                  <div className="h-14 w-14 rounded-lg bg-[#F4EAE6] flex items-center justify-center overflow-hidden shrink-0">
                    {it.imagen_path ? (
                      <img src={fileUrl(it.imagen_path)} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : (
                      <Sandwich className="h-6 w-6 text-[#C85A32]/60" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-[15px] truncate">{it.nombre}</div>
                    <div className="text-[12px] text-[#595959] font-mono">{formatMoney(it.precio)} c/u</div>
                  </div>
                  <div className="flex items-center h-10 bg-[#F9F6F0] rounded-lg border border-[#E5E0D8]">
                    <button type="button" onClick={() => cart.setQty(it.producto_id, it.cantidad - 1)} className="px-2.5 h-full text-[#C85A32]" data-testid={`cli-cart-${it.producto_id}-minus`}>
                      <Minus className="h-4 w-4" />
                    </button>
                    <div className="w-10 text-center font-mono font-bold" data-testid={`cli-cart-${it.producto_id}-qty`}>{it.cantidad}</div>
                    <button type="button" onClick={() => cart.setQty(it.producto_id, it.cantidad + 1)} className="px-2.5 h-full text-[#C85A32]" data-testid={`cli-cart-${it.producto_id}-plus`}>
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="w-24 text-right font-mono font-semibold text-[#1A1A1A] hidden sm:block">
                    {formatMoney(it.cantidad * it.precio)}
                  </div>
                  <button type="button" onClick={() => cart.remove(it.producto_id)} className="ml-1 h-10 w-10 rounded-md hover:bg-[#FBEBEB] text-[#8E2E2E] flex items-center justify-center" data-testid={`cli-cart-${it.producto_id}-remove`} aria-label="Quitar">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="border-[#E5E0D8] rounded-2xl bg-white p-6 h-fit">
            <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#8C8C8C] mb-2">Total · {cart.count} u</div>
            <div className="font-display font-bold text-4xl text-[#C85A32] mb-6" data-testid="cli-cart-total">
              {formatMoney(cart.total)}
            </div>

            <Label className="text-[12px] font-semibold text-[#595959] mb-1.5 block">Nota para el vendedor (opcional)</Label>
            <Textarea
              value={nota}
              onChange={(e) => setNota(e.target.value)}
              placeholder="Ej: para mañana al mediodía"
              className="bg-white min-h-[80px] resize-y mb-4"
              data-testid="cli-cart-nota"
            />

            <Button
              onClick={enviar}
              disabled={sending || suspended}
              className={`w-full h-12 gap-2 rounded-lg font-semibold ${
                suspended
                  ? "bg-[#E5E0D8] text-[#8C8C8C] hover:bg-[#E5E0D8] cursor-not-allowed"
                  : "bg-[#C85A32] hover:bg-[#A64A29] text-white"
              }`}
              data-testid="cli-cart-enviar"
            >
              {suspended ? <ShieldOff className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              {suspended
                ? "Cuenta suspendida"
                : sending
                ? "Enviando..."
                : "Enviar pedido"}
            </Button>
            {suspended && (
              <p
                className="mt-2 text-[11px] text-[#8E2E2E] text-center leading-snug"
                data-testid="cli-cart-suspended-note"
              >
                Hablá con el vendedor para regularizar tu situación.
              </p>
            )}

            <button type="button" onClick={cart.clear} className="w-full mt-3 text-[12px] text-[#8C8C8C] hover:text-[#8E2E2E]" data-testid="cli-cart-clear">
              Vaciar carrito
            </button>
          </Card>
        </div>
      )}
    </div>
  );
}
